import { addToCart as addToCartApi, uploadFile } from '../api/api';
import { ZoneType } from '../api/static/zones';
import { PrintTypeId } from '../api/static/printTypes';
import { objectUrlToBlob } from './ImageUtils';

/**
 * Build the key used to reference a user image file in the fileIdMap
 * @param {Object} customization
 * @returns {string}
 */
export function getUserFileKeyFromCustomization({ id }) {
  return `user-file--${id}`;
}

/**
 * Upload a list of files to the server
 * @param {Object} exports - Object containing files to upload
 * @param {string} csrfToken - CSRF token for API calls
 * @param {Array} customizations - Customizations input (used to upload user images for IMAGE zones)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Map of fileIds
 */
export async function uploadExports(
  exports,
  csrfToken,
  customizations = [],
  onProgress = null
) {
  const fileIdMap = {};

  const uploadsOrder = [
    { key: 'texture72', file: exports.texture72 },
    { key: 'texture300', file: exports.texture300 },
    { key: 'renderLeft', file: exports.renderLeft },
    { key: 'renderCenter', file: exports.renderCenter },
    { key: 'renderRight', file: exports.renderRight },
    { key: 'renderBack', file: exports.renderBack },
  ];

  for (const { key, file } of uploadsOrder) {
    if (!file) {
      continue;
    }

    const fileId = await uploadFile(
      file,
      csrfToken,
      onProgress ? progressEvent => onProgress(key, progressEvent) : undefined
    );
    fileIdMap[key] = fileId;
  }

  // Upload user images from IMAGE zone customizations
  for (const customizationData of customizations) {
    const customization = customizationData.customization || customizationData;

    if (customization.type !== ZoneType.IMAGE || !customization.url) {
      continue;
    }

    const fileKey = getUserFileKeyFromCustomization(customization);
    const blob = await objectUrlToBlob(customization.url);
    const fileId = await uploadFile(blob, csrfToken);
    fileIdMap[fileKey] = fileId;
  }

  return fileIdMap;
}

/**
 * Submit an order to cart
 * @param {Object} params - Parameters for submission
 * @param {Object} params.productVariant - Product variant
 * @param {Object} params.color - Color object
 * @param {Object} params.fileIdMap - Map of uploaded file IDs
 * @param {Array} params.customizations - Customizations array
 * @param {Object} params.configuration - Configuration object
 * @param {boolean} params.isExpressDeliveryEnabled - Express delivery flag
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<void>}
 */
export async function submitToCart(params, csrfToken) {
  const {
    productVariant,
    color,
    fileIdMap,
    customizations,
    isExpressDeliveryEnabled,
  } = params;

  const data = {
    configurationId: productVariant.configurationId,
    variantId: productVariant.id,
    colorId: color.id,
    fileHighQualityId: fileIdMap.texture300 ?? null,
    fileLowQualityId: fileIdMap.texture72 ?? null,
    exportFiles: [
      fileIdMap.renderLeft,
      fileIdMap.renderCenter,
      fileIdMap.renderRight,
      fileIdMap.renderBack,
    ].filter(Boolean),
    screenPrintingColorId:
      productVariant.printTypeId === PrintTypeId.SCREEN_PRINTING ? null : null,
    customizations: customizations,
    expressMode: Boolean(isExpressDeliveryEnabled),
  };

  return await addToCartApi(data, csrfToken);
}

/**
 * Build customizations array for API submission
 * @param {Array} customizationsInput - Input customizations from user
 * @param {Array} targetZones - Target zones from configuration
 * @param {Object} fileIdMap - Map of uploaded file IDs (required for IMAGE zones)
 * @returns {Array} Formatted customizations for API
 */
export function buildCustomizationsData(
  customizationsInput,
  targetZones,
  fileIdMap = {}
) {
  return customizationsInput
    .map((customizationData, index) => {
      const customization =
        customizationData.customization || customizationData;
      const targetZone = targetZones[index];

      if (!targetZone) return null;

      // Skip if no content
      if (
        !customization.text &&
        !customization.url &&
        !targetZone.defaultValue
      ) {
        return null;
      }

      const o = {
        templateZoneId: targetZone.id,
        groupId: targetZone.groupId,
        x: targetZone.x,
        y: targetZone.y,
        scale: targetZone.scale,
        rotation: targetZone.rotation,
        color: targetZone.color,
      };

      if (targetZone.type === ZoneType.TEXT) {
        o.text =
          customization.text !== undefined
            ? customization.text
            : targetZone.defaultValue;
        Object.assign(o, {
          fontSize: targetZone.fontSize,
          bold: targetZone.bold,
          italic: targetZone.italic,
          underline: targetZone.underlined,
          typoId: targetZone.typoId,
        });
      } else if (targetZone.type === ZoneType.IMAGE) {
        const fileId =
          fileIdMap[getUserFileKeyFromCustomization(customization)];

        // Skip image zones without an uploaded file
        if (!fileId) {
          return null;
        }

        Object.assign(o, {
          fileId,
          badQuality: customization.badQuality,
        });
      }

      return o;
    })
    .filter(Boolean);
}
